import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import StageService from "@shared/modules/seating/services/StageService";
import { FormattedSeatType } from "@shared/modules/seating/types/seating-types";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useStore } from "@shared/store/useStore";
import isEmpty from "lodash/isEmpty";
import { useTranslation } from "@shared/utils/internationalization";
import { useSeatingAnalytics } from "../useSeatingAnalytics/useSeatingAnalytics";

const notifier = NotifierFactory.createNotifier();

function useSeatingWaitlistHelpers() {
  const { t } = useTranslation("seating");

  const {
    basketType,
    dynamicHoldSlug,
    enableTracking,
    seatingServiceState,
    seatingEventData,
    setIsUpdatingMap,
    updateSeatingState,
  } = useSeatingState();

  const { trackAddSeat } = useSeatingAnalytics({
    seatingServiceState,
    seatingEventData,
  });

  const { modifyItems, removeItems } = useBasketService();

  /**
   * Attempt to add a waitlisted seat to the cart.
   * This is unique from requestAddSeatPermissions in useSeatingState
   * because the seats to add have the waitlist_seat_segment information attached
   */
  async function requestAddWaitlistedSeats(seats: Array<FormattedSeatType>) {
    const refreshedBasket = useStore.getState().getBasket(basketType);
    const waitlistSegmentsInBasket =
      refreshedBasket?.item_groups?.map(
        (ig) => ig.waitlist_seat_segment?.segment_id
      ) ?? [];

    // don't allow users to add waitlisted seats from different sections to the cart
    // example: seats from section X and section Y
    if (
      !isEmpty(waitlistSegmentsInBasket) &&
      !seats.some((s) =>
        waitlistSegmentsInBasket.includes(s.$data.locationData?.segment_id)
      )
    ) {
      const errorMessage = t(
        "Cannot add waitlisted seats from multiple sections to the cart."
      );
      notifier.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (StageService.isAndroidDevice()) {
      setIsUpdatingMap(true);
    }
    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);

      try {
        const seatsToAdd = seats.map((seat) => {
          return {
            item: {
              ...seat,
              mapItemType: seatingServiceState.mapItemType,
              waitlist_seat_segment: {
                segment_id: seat.$data.locationData?.segment_id,
                segment_identifier: seat.$data.segment_identifier,
              },
            },
            quantity: seat.quantity ?? 1,
          };
        });

        await modifyItems(seatsToAdd, {
          ...refreshedBasket,
          hold_slug: dynamicHoldSlug,
          is_waitlist: true,
        });
      } finally {
        // hide spinner after seats have been added
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Attempt to remove a waitlisted seat from the cart.
   * This is unique from requestRemoveSeatPermissions in useSeatingState
   * because the itemGroup to remove is filtered on the waitlist_seat_segment instead of tt_seat_permissions
   */
  async function requestRemoveWaitlistedSeats(
    segmentId: number,
    itemTypeId: number
  ): Promise<void> {
    if (StageService.isAndroidDevice()) {
      setIsUpdatingMap(true);
    }
    if (!seatingServiceState.isProcessing()) {
      seatingServiceState.setProcessing(true);

      try {
        const refreshedBasket = useStore.getState().getBasket(basketType);

        const itemGroupToRemove =
          refreshedBasket?.item_groups?.filter(
            (group: TicketItemGroupType) =>
              group.waitlist_seat_segment?.segment_id === segmentId &&
              (group.type === itemTypeId ||
                group.membership_level === itemTypeId)
          ) ?? [];

        await removeItems(itemGroupToRemove, {
          ...refreshedBasket,
          hold_slug: dynamicHoldSlug,
          is_waitlist: true,
        });
      } finally {
        // hide spinner after seats have been removed
        setIsUpdatingMap(false);
        seatingServiceState.setProcessing(false);
      }
    }
  }

  /**
   * Attempt to add a seat waitlist to the cart.
   */
  async function addSeatWaitlist(seats: Array<FormattedSeatType>) {
    await requestAddWaitlistedSeats(seats);

    notifier.success(t("Waitlist selection(s) have been modified!"));
    if (enableTracking) {
      trackAddSeat(basketType, seats);
    }
    updateSeatingState();

    // this return type prevents seats from being redrawn
    // (we don't want seats to be redrawn for waitlists)
    return null;
  }

  /**
   * Attempt to remove a seat waitlist from the cart.
   */
  const removeSeatWaitlist = async (
    segmentId: number,
    itemTypeId: number
  ): Promise<null> => {
    await requestRemoveWaitlistedSeats(segmentId, itemTypeId);

    notifier.success(t("Waitlist item(s) removed from cart!"));
    updateSeatingState();

    // this return type prevents seats from being redrawn
    // (we don't want seats to be redrawn for waitlists)
    return null;
  };

  /**
   * Returns the quantity of waitlisted seats in the cart with info_required = True
   */
  function getInfoRequiredWaitlistSeatQty(
    itemId: number,
    segmentId: number,
    fallbackNumber: number
  ) {
    const basket = useStore.getState().getBasket(basketType);
    const itemGroups = basket?.item_groups;

    return Number(
      itemGroups?.filter((ig: TicketItemGroupType) => {
        return (
          (ig.membership_level === itemId || ig.type === itemId) &&
          segmentId === ig.waitlist_seat_segment?.segment_id
        );
      })?.length ?? fallbackNumber
    );
  }

  /**
   * Returns the quantity of waitlisted seats in the cart with info_required = False
   */
  function getWaitlistSeatQty(
    itemId: number,
    segmentId: number,
    fallbackNumber: number
  ) {
    const basket = useStore.getState().getBasket(basketType);
    const itemGroups = basket?.item_groups;

    return Number(
      itemGroups?.find((ig: TicketItemGroupType) => {
        return (
          (ig.membership_level === itemId || ig.type === itemId) &&
          segmentId === ig.waitlist_seat_segment?.segment_id
        );
      })?.quantity ?? fallbackNumber
    );
  }

  return {
    addSeatWaitlist,
    removeSeatWaitlist,
    getInfoRequiredWaitlistSeatQty,
    getWaitlistSeatQty,
  };
}

export default useSeatingWaitlistHelpers;
